import { type FormEvent, useMemo, useState } from 'react';
import { MailPlus, Send, UserCheck, UserPlus, UserX, X } from 'lucide-react';
import type {
  AdminInvitationRecord,
  AdminRoleRecord,
  AdminUserRecord,
  InviteAdminUserInput,
  UnassignedUserRecord,
} from '../types';

interface UsersFeatureProps {
  readonly users: readonly AdminUserRecord[];
  readonly invitations: readonly AdminInvitationRecord[];
  readonly unassignedUsers: readonly UnassignedUserRecord[];
  readonly roles: readonly AdminRoleRecord[];
  readonly canManage: boolean;
  readonly busyUserId: string | null;
  readonly busyRoleUserId: string | null;
  readonly inviting: boolean;
  /**
   * Gibt ein Anfangspasswort zurück, wenn der Server keine Mail verschicken
   * kann und das Konto stattdessen selbst angelegt hat. Sonst `null`.
   */
  readonly onInvite: (input: Omit<InviteAdminUserInput, 'tenantId'>) => Promise<string | null>;
  readonly onToggleActive: (userId: string, active: boolean) => void;
  readonly onSetRoles: (userId: string, roleIds: readonly string[]) => void;
  readonly onAssign: (userId: string, roleIds: readonly string[]) => void;
}

/** Eine Zeile für einen noch nicht zugeordneten Auth-Benutzer: Rolle(n) wählen + zuordnen. */
function AssignUserRow({ user, roles, busy, canManage, onAssign }: {
  readonly user: UnassignedUserRecord;
  readonly roles: readonly AdminRoleRecord[];
  readonly busy: boolean;
  readonly canManage: boolean;
  readonly onAssign: (userId: string, roleIds: readonly string[]) => void;
}) {
  const defaultRoleId = useMemo(
    () => roles.find((role) => role.name === 'Verwaltung')?.id ?? roles[0]?.id ?? '',
    [roles],
  );
  const [selected, setSelected] = useState<readonly string[]>(defaultRoleId ? [defaultRoleId] : []);
  return (
    <article className="admin-invitation is-pending">
      <span className="admin-invitation__icon"><UserPlus size={16} /></span>
      <div>
        <strong>{user.displayName}</strong>
        <small>{user.email} · noch keine Rolle</small>
      </div>
      <details className="admin-user-role-editor">
        <summary>Rolle wählen</summary>
        <div className="admin-user-role-options">
          {roles.map((role) => (
            <label key={role.id}>
              <input
                type="checkbox"
                checked={selected.includes(role.id)}
                disabled={!canManage || busy}
                onChange={(event) => setSelected((current) => event.target.checked
                  ? [...new Set([...current, role.id])]
                  : current.filter((roleId) => roleId !== role.id))}
              />
              <span>{role.name}</span>
            </label>
          ))}
        </div>
      </details>
      <button
        type="button"
        className="admin-primary-button"
        disabled={!canManage || busy || selected.length === 0}
        onClick={() => onAssign(user.id, selected)}
      >
        <UserCheck size={15} /> {busy ? 'Ordnet zu …' : 'Zuordnen'}
      </button>
    </article>
  );
}

function roleNamesByIds(roleIds: readonly string[], roles: readonly AdminRoleRecord[]): string {
  return roleIds
    .map((roleId) => roles.find((role) => role.id === roleId)?.name)
    .filter((name): name is string => Boolean(name))
    .join(', ') || 'Keine Rolle';
}

function roleNames(user: AdminUserRecord, roles: readonly AdminRoleRecord[]): string {
  return roleNamesByIds(user.roleIds, roles);
}

function nextRoleIds(user: AdminUserRecord, roleId: string, assigned: boolean): readonly string[] {
  if (assigned) return [...new Set([...user.roleIds, roleId])];
  return user.roleIds.filter((currentRoleId) => currentRoleId !== roleId);
}

function invitationStatus(record: AdminInvitationRecord): string {
  if (record.status === 'pending') return 'Wird vorbereitet';
  if (record.status === 'sent') return 'Einladung versandt';
  if (record.status === 'assigned') return 'Bestehender Zugang zugeordnet';
  return 'Fehlgeschlagen';
}

export function UsersFeature({
  users,
  invitations,
  unassignedUsers,
  roles,
  canManage,
  busyUserId,
  busyRoleUserId,
  inviting,
  onInvite,
  onToggleActive,
  onSetRoles,
  onAssign,
}: UsersFeatureProps) {
  const defaultRoleId = useMemo(
    () => roles.find((role) => role.name === 'Verwaltung')?.id ?? roles[0]?.id ?? '',
    [roles],
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [inviteRoleIds, setInviteRoleIds] = useState<readonly string[]>(defaultRoleId ? [defaultRoleId] : []);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [handover, setHandover] = useState<{ email: string; password: string } | null>(null);

  function openInvitation() {
    setDisplayName('');
    setEmail('');
    setInviteRoleIds(defaultRoleId ? [defaultRoleId] : []);
    setInviteError(null);
    setInviteOpen(true);
  }

  function toggleInviteRole(roleId: string, checked: boolean) {
    setInviteRoleIds((current) => checked
      ? [...new Set([...current, roleId])]
      : current.filter((currentRoleId) => currentRoleId !== roleId));
  }

  async function submitInvitation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setInviteError(null);
    if (inviteRoleIds.length === 0) {
      setInviteError('Bitte mindestens eine Rolle auswählen.');
      return;
    }

    try {
      const eingeladen = email.trim();
      const initialPassword = await onInvite({
        displayName: displayName.trim(),
        email: eingeladen,
        roleIds: inviteRoleIds,
      });
      // Ohne Mailversand ist dies der einzige Moment, in dem das Passwort zu
      // sehen ist -- es steht nirgends gespeichert.
      setHandover(initialPassword ? { email: eingeladen, password: initialPassword } : null);
      setInviteOpen(false);
    } catch (error: unknown) {
      setInviteError(error instanceof Error && error.message
        ? error.message
        : 'Die Einladung konnte nicht erstellt werden.');
    }
  }

  return (
    <section className="admin-feature" aria-labelledby="admin-users-title">
      <div className="admin-feature__heading">
        <div>
          <span className="admin-eyebrow">Benutzer & Zugänge</span>
          <h2 id="admin-users-title">Wer darf für diesen Mandanten arbeiten?</h2>
        </div>
        <button
          className="admin-primary-button"
          type="button"
          disabled={!canManage || inviting || roles.length === 0}
          onClick={openInvitation}
          title={canManage ? undefined : 'Keine Berechtigung zum Einladen von Benutzern'}
        >
          <MailPlus size={16} />
          {inviting ? 'Einladung läuft …' : 'Benutzer einladen'}
        </button>
      </div>

      {handover && (
        <div className="admin-handover" role="status">
          <div>
            <strong>Zugang angelegt für {handover.email}</strong>
            <p>
              Dieser Server verschickt keine E-Mails. Bitte das Anfangspasswort
              persönlich weitergeben — es steht nirgends gespeichert und ist
              nach dem Schließen nicht mehr abrufbar.
            </p>
            <code className="admin-handover__secret">{handover.password}</code>
          </div>
          <button
            className="admin-handover__close"
            type="button"
            aria-label="Anfangspasswort ausblenden"
            onClick={() => setHandover(null)}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {invitations.length > 0 && (
        <div className="admin-invitation-list" aria-label="Einladungen">
          {invitations.map((invitation) => (
            <article key={invitation.id} className={`admin-invitation is-${invitation.status}`}>
              <span className="admin-invitation__icon"><MailPlus size={16} /></span>
              <div>
                <strong>{invitation.displayName}</strong>
                <small>{invitation.email} · {roleNamesByIds(invitation.roleIds, roles)}</small>
              </div>
              <span>{invitationStatus(invitation)}</span>
              {invitation.errorMessage ? <p>{invitation.errorMessage}</p> : null}
            </article>
          ))}
        </div>
      )}

      {unassignedUsers.length > 0 && (
        <div className="admin-unassigned">
          <div className="admin-feature__heading">
            <div>
              <span className="admin-eyebrow">Noch nicht zugeordnet</span>
              <h3>Auth-Benutzer ohne Rolle</h3>
            </div>
          </div>
          <p className="admin-hint">Direkt in Supabase angelegte Benutzer haben noch keinen Zugriff auf diesen Mandanten. Rolle wählen und zuordnen — danach erscheinen sie oben in der Liste.</p>
          <div className="admin-invitation-list" aria-label="Noch nicht zugeordnete Benutzer">
            {unassignedUsers.map((user) => (
              <AssignUserRow key={user.id} user={user} roles={roles} busy={busyRoleUserId === user.id} canManage={canManage} onAssign={onAssign} />
            ))}
          </div>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Benutzer</th>
              <th>Rollen</th>
              <th>Letzte Anmeldung</th>
              <th>Status</th>
              <th><span className="sr-only">Aktionen</span></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="admin-user-cell">
                    <span>{user.displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()}</span>
                    <div>
                      <strong>
                        {user.displayName}
                        {user.isCurrentUser ? <em className="admin-you-badge">Du</em> : null}
                      </strong>
                      <small>{user.email}</small>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="admin-user-role-cell">
                    <span>{roleNames(user, roles)}</span>
                    <details className="admin-user-role-editor">
                      <summary>Rollen ändern</summary>
                      <div className="admin-user-role-options">
                        {roles.map((role) => {
                          const checked = user.roleIds.includes(role.id);
                          const wouldRemoveLastRole = checked && user.roleIds.length === 1;
                          return (
                            <label key={role.id}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={
                                  !canManage
                                  || user.isCurrentUser
                                  || busyRoleUserId === user.id
                                  || wouldRemoveLastRole
                                }
                                onChange={(event) => onSetRoles(
                                  user.id,
                                  nextRoleIds(user, role.id, event.target.checked),
                                )}
                              />
                              <span>{role.name}</span>
                            </label>
                          );
                        })}
                        {user.isCurrentUser ? <small>Die eigenen Rollen sind gegen Selbstsperre geschützt.</small> : null}
                        {busyRoleUserId === user.id ? <small>Rollen werden gespeichert …</small> : null}
                      </div>
                    </details>
                  </div>
                </td>
                <td>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('de-DE') : 'Noch nie'}</td>
                <td>
                  <span className={`admin-status ${user.active ? 'is-active' : 'is-inactive'}`}>
                    {user.active ? 'Aktiv' : 'Gesperrt'}
                  </span>
                </td>
                <td className="admin-table__action">
                  <button
                    className="admin-secondary-button"
                    type="button"
                    disabled={!canManage || user.isCurrentUser || busyUserId === user.id}
                    onClick={() => onToggleActive(user.id, !user.active)}
                    title={
                      user.isCurrentUser
                        ? 'Das eigene Benutzerkonto ist gegen Selbstsperre geschützt'
                        : canManage
                          ? undefined
                          : 'Keine Berechtigung zum Verwalten von Benutzern'
                    }
                  >
                    {user.active ? <UserX size={15} /> : <UserCheck size={15} />}
                    {busyUserId === user.id ? 'Speichert …' : user.active ? 'Sperren' : 'Aktivieren'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!canManage && <p className="admin-hint">Du besitzt Leserechte. Änderungen erfordern <code>admin.users.manage</code>.</p>}

      {inviteOpen && (
        <div className="admin-modal-backdrop" role="presentation" onMouseDown={() => !inviting && setInviteOpen(false)}>
          <section
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-invite-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <span className="admin-eyebrow">Neuer FORQ-Zugang</span>
                <h3 id="admin-invite-title">Benutzer einladen</h3>
              </div>
              <button type="button" className="admin-modal__close" onClick={() => setInviteOpen(false)} disabled={inviting} aria-label="Schließen">
                <X size={18} />
              </button>
            </header>
            <form onSubmit={submitInvitation}>
              <label>
                Name
                <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} minLength={2} required autoFocus />
              </label>
              <label>
                E-Mail-Adresse
                <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
              </label>
              <fieldset>
                <legend>Rollen in diesem Mandanten</legend>
                <div className="admin-invite-roles">
                  {roles.map((role) => (
                    <label key={role.id}>
                      <input
                        type="checkbox"
                        checked={inviteRoleIds.includes(role.id)}
                        onChange={(event) => toggleInviteRole(role.id, event.target.checked)}
                      />
                      <span><strong>{role.name}</strong><small>{role.description}</small></span>
                    </label>
                  ))}
                </div>
              </fieldset>
              {inviteError ? <div className="admin-modal__error" role="alert">{inviteError}</div> : null}
              <div className="admin-modal__actions">
                <button type="button" className="admin-secondary-button" onClick={() => setInviteOpen(false)} disabled={inviting}>Abbrechen</button>
                <button type="submit" className="admin-primary-button" disabled={inviting || inviteRoleIds.length === 0}>
                  <Send size={16} /> {inviting ? 'Wird eingeladen …' : 'Einladung senden'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </section>
  );
}
